import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
    Box, Button, Checkbox, Chip, Divider,
    FormControl, ListItemText, MenuItem,
    OutlinedInput, Select, Typography,
} from '@mui/material';
import LegendToggleIcon from '@mui/icons-material/LegendToggle';
import {
    setSide, setSelectedPolygons, selectAllPolygons,
    setViewYear, resetViewYear,
} from '../../reducers/Beacon4BlendedFiltersReducer';
import { loadPolygons, loadKpiData } from '../../actions/Beacon4BlendedActions';
import Beacon4BlendedExportButton from './Beacon4BlendedExportButton';

const Label = ({ children }) => (
    <Typography sx={{ fontSize: 10, fontWeight: 500, color: '#8fa3bc', textTransform: 'uppercase', letterSpacing: '0.06em', mb: '3px' }}>
        {children}
    </Typography>
);

export default function Beacon4BlendedFilterBar({ showLegend, onToggleLegend, containerRef }) {
    const dispatch = useDispatch();
    const { platform, type, side, selectedPolygons, sides, polygons, loadingPolygons, viewYear } = useSelector(s => s.filters);
    const { series } = useSelector(s => s.kpiData);

    // Derive available years from actual data — no hardcoding
    const availableYears = React.useMemo(() => {
        if (!series?.length) return [];
        const years = new Set();
        series.forEach(s => s.data?.forEach(d => { if (!d.isProjected) years.add(d.year); }));
        return [...years].sort();
    }, [series]);

    // Local draft selection — only fires API on dropdown close
    const [draft, setDraft] = useState(selectedPolygons);
    useEffect(() => { setDraft(selectedPolygons); }, [selectedPolygons]);

    useEffect(() => { if (side) dispatch(loadPolygons({ platform, type, side })); }, [side]);

    const handleSideChange = (e) => {
        dispatch(setSide(e.target.value));
        dispatch(loadPolygons({ platform, type, side: e.target.value }));
    };

    const handleToggleAll = () => {
        // All = empty array; if already all selected, keep all (no toggle needed)
        setDraft([]);
    };

    const handleTogglePolygon = (p) => {
        setDraft(prev =>
            prev.includes(p) ? prev.filter(x => x !== p) : [...prev, p]
        );
    };

    const handleClose = () => {
        dispatch(setSelectedPolygons(draft));
        dispatch(loadKpiData({ platform, type, side, polygons: draft }));
    };

    const isAllSelected = draft.length === 0;

    const renderValue = () => {
        if (draft.length === 0) return <Chip size="small" label="All polygons" sx={{ height: 20, fontSize: 10, bgcolor: '#E3F2FD', color: '#0D47A1' }} />;
        if (draft.length === 1) return draft[0];
        return <Chip size="small" label={`AVG · ${draft.length} polygons`} sx={{ height: 20, fontSize: 10, bgcolor: '#E3F2FD', color: '#0D47A1' }} />;
    };

    return (
        <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 1.5, px: 2, py: 1,
                   bgcolor: '#fff', borderBottom: '1px solid #e3e8ef', flexWrap: 'wrap' }}>

            {/* Side */}
            <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                <Label>Side</Label>
                <FormControl size="small" sx={{ minWidth: 130, bgcolor: '#fff' }}>
                    <Select value={side} onChange={handleSideChange} displayEmpty sx={{ height: 30, fontSize: 11 }}>
                        {sides.map(s => <MenuItem key={s} value={s}>{s}</MenuItem>)}
                    </Select>
                </FormControl>
            </Box>

            {/* Polygon multi-select — controlled locally until close */}
            <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                <Label>Polygon</Label>
                <FormControl size="small" sx={{ minWidth: 180, bgcolor: '#fff' }} disabled={!side || loadingPolygons}>
                    <Select
                        multiple
                        value={draft}
                        renderValue={renderValue}
                        onClose={handleClose}
                        input={<OutlinedInput />}
                        MenuProps={{ PaperProps: { style: { maxHeight: 300 } } }}
                        sx={{ height: 30, fontSize: 11 }}
                        // Suppress default onChange — we manage state manually
                        onChange={() => {}}
                    >
                        {/* All polygons row */}
                        <MenuItem dense onClick={handleToggleAll}
                            sx={{ bgcolor: isAllSelected ? '#E3F2FD' : 'transparent' }}>
                            <Checkbox checked={isAllSelected} size="small" sx={{ p: 0, mr: 1, pointerEvents: 'none' }} />
                            <ListItemText primary="All polygons" primaryTypographyProps={{ fontSize: 12, fontWeight: 600, color: '#0D47A1' }} />
                        </MenuItem>
                        <Box sx={{ height: '1px', bgcolor: '#e3e8ef', mx: 1, my: 0.5 }} />
                        {polygons.map(p => (
                            <MenuItem key={p} dense onClick={() => handleTogglePolygon(p)}
                                sx={{ bgcolor: (!isAllSelected && draft.includes(p)) ? '#f0f7ff' : 'transparent' }}>
                                <Checkbox checked={!isAllSelected && draft.includes(p)} size="small" sx={{ p: 0, mr: 1, pointerEvents: 'none' }} />
                                <ListItemText primary={p} primaryTypographyProps={{ fontSize: 12 }} />
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
            </Box>

            <Divider orientation="vertical" flexItem sx={{ mx: 0.5, my: 0.25 }} />

            {/* Year filter — from actual data only */}
            <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                <Label>Year</Label>
                <FormControl size="small" sx={{ minWidth: 100, bgcolor: '#fff' }}>
                    <Select
                        value={viewYear ?? 'all'}
                        onChange={e => e.target.value === 'all' ? dispatch(resetViewYear()) : dispatch(setViewYear(e.target.value))}
                        sx={{ height: 30, fontSize: 11 }}
                    >
                        <MenuItem value="all" sx={{ fontSize: 12 }}>All years</MenuItem>
                        {availableYears.map(y => (
                            <MenuItem key={y} value={y} sx={{ fontSize: 12 }}>{y}</MenuItem>
                        ))}
                    </Select>
                </FormControl>
            </Box>

            <Box sx={{ flex: 1 }} />

            <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-end' }}>
                <Beacon4BlendedExportButton containerRef={containerRef} title={`Beacon4_MonthlyBlended_${side}`} />
                <Button variant={showLegend ? 'contained' : 'outlined'} size="small"
                    startIcon={<LegendToggleIcon sx={{ fontSize: 14 }} />} onClick={onToggleLegend}
                    sx={{ fontSize: 11, fontWeight: 600, height: 30, px: 1.5, textTransform: 'none', boxShadow: 'none',
                         ...(showLegend ? { bgcolor: '#1565c0', '&:hover': { bgcolor: '#1045a0' } }
                                        : { borderColor: '#cdd5e0', color: '#4a5568', '&:hover': { borderColor: '#1565c0', color: '#1565c0' } }) }}>
                    Legend
                </Button>
            </Box>
        </Box>
    );
}